import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '@/contexts/AuthContext';
import { useLoyaltyProfile } from '@/hooks/useLoyaltyProfile';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Crown, 
  Gift, 
  Star, 
  LogOut, 
  QrCode, 
  History, 
  Trophy,
  Coffee,
  Cake,
  Percent,
  ScanLine
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import WelcomeModal from './WelcomeModal';
import InstagramBonus from './InstagramBonus';
import LevelProgressCard from './LevelProgressCard';
import TicketScanner from './TicketScanner';
import UserPrizesSection from './UserPrizesSection';

const WELCOME_SHOWN_KEY = 'club_welcome_shown';

const ClubDashboard = () => {
  const { user, signOut } = useAuth();
  const { profile, pointsHistory, rewards, availableRewards, loading, refetch } = useLoyaltyProfile();
  const [showWelcome, setShowWelcome] = useState(false);

  // Check if this is the first visit for this user
  useEffect(() => {
    if (profile && user) {
      const welcomeKey = `${WELCOME_SHOWN_KEY}_${user.id}`;
      const hasSeenWelcome = sessionStorage.getItem(welcomeKey);
      
      // Show welcome if profile was just created (within last 60 seconds) and hasn't seen it
      const profileAge = Date.now() - new Date(profile.created_at).getTime();
      const isNewProfile = profileAge < 60000; // 60 seconds
      
      if (!hasSeenWelcome && isNewProfile) {
        setShowWelcome(true);
        sessionStorage.setItem(welcomeKey, 'true');
      }
    }
  }, [profile, user]);

  const handleCloseWelcome = () => {
    setShowWelcome(false);
  };

  const handleLogout = async () => {
    await signOut();
    toast.success('Sesión cerrada');
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'bebida':
        return <Coffee className="h-5 w-5" />;
      case 'dulce':
        return <Cake className="h-5 w-5" />;
      case 'descuento':
        return <Percent className="h-5 w-5" />;
      default:
        return <Gift className="h-5 w-5" />;
    }
  };

  const getPointsTypeColor = (type: string) => {
    switch (type) {
      case 'earned':
        return 'bg-green-100 text-green-800';
      case 'redeemed':
        return 'bg-red-100 text-red-800';
      case 'bonus':
      case 'welcome':
      case 'instagram':
        return 'bg-secondary/20 text-secondary';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-muted rounded-lg" />
            <div className="h-64 bg-muted rounded-lg" />
          </div>
        </div>
      </Layout>
    );
  }

  // If no profile yet, show creation pending message
  if (!profile) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-md mx-auto text-center">
            <CardHeader>
              <CardTitle>Creando tu perfil de socio...</CardTitle>
              <CardDescription>
                Estamos preparando tu cuenta. Por favor, recarga la página en unos segundos.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => refetch()}>Actualizar</Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Welcome Modal for new users */}
      <WelcomeModal
        open={showWelcome}
        onClose={handleCloseWelcome}
        firstName={profile.first_name}
        points={5}
      />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-serif font-bold text-foreground">
              ¡Hola, {profile.first_name}!
            </h1>
            <p className="text-muted-foreground">
              Socio #{profile.member_number || 'Pendiente'}
            </p>
          </div>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>

        {/* Level Progress Card */}
        <div className="mb-8">
          <LevelProgressCard 
            totalPointsEarned={profile.total_points_earned}
            currentLevel={(profile as any).current_level || 1}
          />
        </div>

        {/* Points Card */}
        <Card className="mb-8 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-6 md:p-8 relative z-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-primary-foreground/80 text-sm mb-1">Puntos disponibles</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl md:text-6xl font-bold">{profile.points_balance}</span>
                  <span className="text-xl text-primary-foreground/80">puntos</span>
                </div>
              </div>
              
              {/* Member QR Code */}
              <div className="bg-background/95 p-4 rounded-xl shadow-lg">
                <QRCodeSVG
                  value={`REYES-MEMBER:${profile.user_id}`}
                  size={120}
                  level="H"
                  includeMargin={false}
                />
                <p className="text-center text-xs text-foreground mt-2 font-medium">
                  Tu QR de Socio
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instagram Bonus Component */}
        <InstagramBonus
          userId={user?.id || ''}
          instagramClaimed={profile.instagram_claimed}
          onPointsClaimed={refetch}
        />

        {/* Main Content Tabs */}
        <Tabs defaultValue="scan" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-lg">
            <TabsTrigger value="scan">
              <ScanLine className="h-4 w-4 mr-1" />
              Escanear
            </TabsTrigger>
            <TabsTrigger value="prizes">
              <Gift className="h-4 w-4 mr-1" />
              Premios
            </TabsTrigger>
            <TabsTrigger value="qr">
              <QrCode className="h-4 w-4 mr-1" />
              Mi QR
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="h-4 w-4 mr-1" />
              Historial
            </TabsTrigger>
          </TabsList>

          {/* Scan Ticket Tab */}
          <TabsContent value="scan">
            <TicketScanner onPointsEarned={() => refetch()} />
          </TabsContent>

          {/* Level Prizes Tab */}
          <TabsContent value="prizes" className="space-y-6">
            <UserPrizesSection />

            {/* Standard rewards section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-secondary" />
                  Canjea tus Puntos
                </CardTitle>
                <CardDescription>Recompensas disponibles en tienda</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {rewards.map((reward) => {
                    const canRedeem = profile.points_balance >= reward.points_required;
                    return (
                      <div 
                        key={reward.id} 
                        className={`flex items-start gap-3 p-3 rounded-lg border ${canRedeem ? 'border-secondary/50 bg-secondary/5' : 'opacity-60'}`}
                      >
                        <div className={`p-2 rounded-lg ${canRedeem ? 'bg-secondary/20 text-secondary' : 'bg-muted text-muted-foreground'}`}>
                          {getCategoryIcon(reward.category)}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground text-sm">{reward.name}</h4>
                          <Badge variant={canRedeem ? 'default' : 'outline'} className="mt-1">
                            {reward.points_required} pts
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* QR Tab */}
          <TabsContent value="qr">
            <Card className="max-w-md mx-auto">
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center gap-2">
                  <Crown className="h-5 w-5 text-secondary" />
                  Tu Carnet de Socio
                </CardTitle>
                <CardDescription>
                  Muestra este QR en caja para acumular puntos
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <div className="bg-card border-2 border-border p-6 rounded-2xl shadow-lg">
                  <QRCodeSVG
                    value={`REYES-MEMBER:${profile.user_id}`}
                    size={200}
                    level="H"
                    includeMargin={false}
                  />
                </div>
                <div className="mt-4 text-center">
                  <p className="font-semibold text-foreground">
                    {profile.first_name} {profile.last_name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Socio #{profile.member_number || 'Pendiente'}
                  </p>
                  <Badge variant="secondary" className="mt-2">
                    {profile.points_balance} puntos
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Historial de Puntos</CardTitle>
                <CardDescription>
                  Tus últimos movimientos
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pointsHistory.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Aún no tienes movimientos. ¡Haz tu primera compra!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {pointsHistory.map((item) => (
                      <div 
                        key={item.id} 
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Badge className={getPointsTypeColor(item.type)}>
                            {item.points > 0 ? '+' : ''}{item.points}
                          </Badge>
                          <div>
                            <p className="font-medium text-foreground text-sm">
                              {item.description}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(item.created_at), "d 'de' MMMM, HH:mm", { locale: es })}
                            </p>
                          </div>
                        </div>
                        {item.purchase_amount && (
                          <span className="text-sm text-muted-foreground">
                            {item.purchase_amount.toFixed(2)}€
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ClubDashboard;
